import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Form, Input, InputNumber, Modal, Select, Spin, Switch, Typography, message } from "antd";
import { useState } from "react";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { supabase } from "~/store/initializeStore";

// TRE-1613 — the same intent the scheduled_ai_agent_message automation task carries
// (prompt / tone / length / upsell), only fired now for one client. The advisor writes
// the opening message and it lands as a fresh conversation the client can answer.

export interface StartAdvisorConversationValues {
  traineeId: string;
  promptTemplate: string;
  tone?: string;
  maxLength?: number;
  upsellHint?: boolean;
}

type TraineeOption = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  userId: string | null;
};

const TONE_OPTIONS = ["ciepło i motywująco", "konkretnie i rzeczowo", "lekko, z humorem", "troskliwie, bez presji"].map(
  (tone) => ({ label: tone, value: tone }),
);

const traineeLabel = (trainee: TraineeOption) => {
  const name = [trainee.firstName, trainee.lastName].filter(Boolean).join(" ");
  return name ? `${name} — ${trainee.email}` : trainee.email;
};

export const StartAdvisorConversationModal = ({
  defaultTraineeId,
  onClose,
  onStarted,
  open,
}: {
  defaultTraineeId?: string | null;
  onClose: () => void;
  onStarted: () => void;
  open: boolean;
}): React.ReactElement => {
  const [form] = Form.useForm<StartAdvisorConversationValues>();
  const [search, setSearch] = useState("");

  // Server-side search: the client list is far too large to load up front.
  const traineesQuery = useQuery({
    queryKey: ["advisorConversationTrainees", search, defaultTraineeId],
    retry: false,
    queryFn: async () => {
      let query = supabase
        .from("trainee")
        .select('id, email, "firstName", "lastName", status, "userId"')
        .eq("status", "ACTIVE")
        .not("userId", "is", null)
        .limit(20);

      // `,`, `(`, `)` and `*` are PostgREST `or=` separators/wildcards — strip them
      // instead of letting a typed comma turn into a malformed filter.
      const term = search
        .trim()
        .replace(/[,()*]/g, " ")
        .trim();
      if (term) {
        query = query.or(`email.ilike.%${term}%,firstName.ilike.%${term}%,lastName.ilike.%${term}%`);
      } else if (defaultTraineeId) {
        query = query.eq("id", defaultTraineeId);
      } else {
        query = query.order("createdAt", { ascending: false });
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []) as TraineeOption[];
    },
  });

  const startMutation = useMutation({
    mutationFn: async (values: StartAdvisorConversationValues) => {
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      const response = await fetch(`${getClientWebApiBaseUrl()}/api/advisor/start-conversation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          traineeId: values.traineeId,
          promptTemplate: values.promptTemplate,
          tone: values.tone || undefined,
          maxLength: values.maxLength || undefined,
          upsellHint: values.upsellHint === true,
        }),
      });

      const result = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(result.error || "Nie udało się uruchomić rozmowy");
      }
    },
    onSuccess: () => {
      void message.success("Agent pisze wiadomość — rozmowa pojawi się na liście za chwilę.");
      form.resetFields();
      onStarted();
      onClose();
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się uruchomić rozmowy");
    },
  });

  return (
    <Modal
      title="Nowa rozmowa przez agenta"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => form.submit()}
      okText="Uruchom"
      cancelText="Anuluj"
      confirmLoading={startMutation.isLoading}
      width={640}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Opiekunka sama napisze wiadomość"
        description="Agent pobierze dane klientki (plan, postępy, gamifikacja) i otworzy rozmowę w czacie Opiekunki. Klientka może na nią odpisać i rozmawiać dalej."
      />

      <Form<StartAdvisorConversationValues>
        form={form}
        layout="vertical"
        initialValues={{ traineeId: defaultTraineeId ?? undefined, maxLength: 600, upsellHint: false }}
        onFinish={(values) => startMutation.mutate(values)}
      >
        <Form.Item name="traineeId" label="Klientka" rules={[{ required: true, message: "Wybierz klientkę" }]}>
          <Select
            showSearch
            filterOption={false}
            onSearch={setSearch}
            placeholder="Szukaj po e-mailu lub imieniu"
            notFoundContent={traineesQuery.isFetching ? <Spin size="small" /> : "Brak wyników"}
            options={(traineesQuery.data ?? []).map((trainee) => ({
              label: traineeLabel(trainee),
              value: trainee.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="promptTemplate"
          label="Intencja dla agenta"
          rules={[{ required: true, message: "Opisz, o czym ma napisać agent" }]}
          extra="Tak jak w automatyzacji programu — opisz cel wiadomości, nie jej treść."
        >
          <Input.TextArea
            rows={4}
            placeholder="np. zapytaj jak jej minął pierwszy tydzień, pochwal konkretne treningi i zaproponuj mały krok na kolejny"
          />
        </Form.Item>

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <Form.Item name="tone" label="Ton (opcjonalnie)">
            <Select allowClear options={TONE_OPTIONS} placeholder="domyślny ton Opiekunki" />
          </Form.Item>
          <Form.Item name="maxLength" label="Maksymalna długość (znaki)">
            <InputNumber min={120} max={2000} step={50} className="w-full" />
          </Form.Item>
        </div>

        <Form.Item name="upsellHint" label="Pozwól wpleść ofertę" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Typography.Text type="secondary" className="text-xs">
          Oferty pochodzą wyłącznie z zakładki Oferty (narzędzie getOffers) — agent nie wymyśla cen ani promocji.
        </Typography.Text>
      </Form>
    </Modal>
  );
};
